import { TooltipParams } from '@anm/components/charts/ChartLine';
import { D3MouseEvent } from '@anm/components/charts/types';
import formatTime from '@anm/helpers/time/formatTime';
import { select } from 'd3-selection';
import { RefObject } from 'react';

export const getChartData = (data: number[] = []) =>
  data?.map(item => ({
    value: item,
    label: `${item}`
  }));

export const drawEngagementChartTooltip = ({ svg, data, width, height, chartRef, maxValue }: TooltipParams) => (
  videoRef: RefObject<HTMLVideoElement>,
  projectDuration: number
) => {
  const tooltipTopHeight = 30;

  chartRef?.current?.querySelectorAll('.label')?.forEach(el => el.remove());
  chartRef?.current?.querySelectorAll('.tooltip')?.forEach(el => el.remove());
  chartRef?.current?.querySelectorAll('.vertical-line')?.forEach(el => el.remove());

  const verticalLineBottom = select(chartRef?.current!)
    .append('div')
    .attr('class', `vertical-line bottom`)
    .style('bottom', '4px');

  const verticalLineTop = select(chartRef?.current!)
    .append('div')
    .attr('class', `vertical-line top`)
    .style('top', '0');

  const tooltipTop = select(chartRef?.current!)
    .append('div')
    .attr('class', `tooltip top`)
    .style('height', `${tooltipTopHeight}px`)
    .style('transform', 'translateX(-50%)')
    .style('opacity', '0')
    .style('color', '#fff');

  const tooltipBottom = select(chartRef?.current!)
    .append('div')
    .attr('class', `tooltip bottom`)
    .style('bottom', '-20px')
    .style('transform', 'translateX(-50%)')
    .style('opacity', '1')
    .style('color', '#919EAA');

  const startXLabel = select(chartRef?.current!)
    .append('span')
    .attr('class', `label`)
    .style('left', '0')
    .html(formatTime(0));

  const ednXLabel = select(chartRef?.current!)
    .append('span')
    .attr('class', `label`)
    .style('right', '0')
    .html(formatTime(projectDuration));

  const handleMouseOver = () => {
    verticalLineTop.style('opacity', '1');
    verticalLineBottom.style('opacity', '1');
    tooltipTop.style('opacity', '1');
    tooltipBottom.style('opacity', '1');
    startXLabel.style('opacity', '0');
    ednXLabel.style('opacity', '0');
  };
  const handleMouseOut = () => {
    verticalLineTop.style('opacity', '0');
    verticalLineBottom.style('opacity', '0');
    tooltipTop.style('opacity', '0');
    tooltipBottom.style('opacity', '0');
    startXLabel.style('opacity', '1');
    ednXLabel.style('opacity', '1');
  };

  const handleMouseMove = ({ layerX }: D3MouseEvent) => {
    const dataLength = data.length - 1;
    const step = width / dataLength;
    const showLastStep = layerX > width - step / 2;
    const dataPosition = ~~(layerX / step);
    const left = showLastStep ? width - 1 : dataPosition * step;

    const value = showLastStep ? data[dataLength].value : data[dataPosition]?.value;
    const ratio = value / maxValue;
    const engagementPercentage = Math.ceil(ratio * 100);

    const { width: tooltipTopWidth } = tooltipTop.node()?.getBoundingClientRect()!;
    const tooltipTopBottomPosition =
      height * ratio + 40 + tooltipTopHeight > height ? height - tooltipTopHeight : height * ratio + 40;

    const isOnLeftSide = layerX < tooltipTopWidth;
    const isOnRightSide = layerX > width - tooltipTopWidth * 0.5;

    const topTooltipLeft = isOnLeftSide
      ? tooltipTopWidth / 2
      : isOnRightSide
      ? width - tooltipTopWidth * 0.5 - step
      : layerX - step;

    const leftPx = Math.ceil(topTooltipLeft / step) * step;

    const video = videoRef.current;
    const videoDuration = video ? video.duration : 0;
    const videoPersentage = video ? videoDuration * (dataPosition * step) : 0;
    const videoCurrentTime = ~~(videoPersentage / width);
    const bottomTooltipHtml = `<span>${formatTime(dataPosition)}</span>`;

    const topTooltipHtml = `
    <div>
      <p>${engagementPercentage}%<p>
      <p>${data[dataPosition]?.label} views</p>
    </div>
  `;

    video && (video.currentTime = videoCurrentTime);

    verticalLineTop
      .style('left', `${left}px`)
      .style('height', `${height - tooltipTopBottomPosition - tooltipTopHeight}px`);

    verticalLineBottom.style('left', `${left}px`).style('top', `${height - tooltipTopBottomPosition + 10}px`);

    tooltipTop.style('bottom', `${tooltipTopBottomPosition}px`);
    video && tooltipBottom.style('left', `${left}px`).html(bottomTooltipHtml);
    tooltipTop
      .style('width', `fit-content`)
      .style('left', `${leftPx}px`)
      .html(topTooltipHtml);
  };

  svg
    .append('rect')
    .attr('class', 'overlay')
    .attr('height', height)
    .attr('width', width)
    .style('fill', 'transparent')
    .on('mouseover', handleMouseOver)
    .on('mouseout', handleMouseOut)
    .on('mousemove', handleMouseMove);
};
